import { createContext, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useReducedMotion } from "framer-motion";
import { api } from "@/lib/api";

export function useOpenPreset() {
  const [, setParams] = useSearchParams();
  return (p) => setParams((prev) => { prev.set("preset", p.id); return prev; });
}

export const useMediaConfig = () => useQuery({ queryKey: ["media-config"], queryFn: () => api.get("/media/config"), staleTime: 30_000 });

export const useGenerations = () =>
  useQuery({
    queryKey: ["media-generations"],
    queryFn: () => api.get("/media/generations"),
    refetchInterval: (q) => ((q.state.data?.items || []).some((g) => g.status === "running") ? 3000 : false),
  });

export const usePresets = () => useQuery({ queryKey: ["studio-presets"], queryFn: () => api.get("/studio/presets"), staleTime: 5 * 60_000 });

export const imageOpen = (config) => Boolean(config && config.image.access !== "none");
export const videoOpen = (config) => Boolean(config?.video?.available);
export const presetOpen = (preset, config) => (preset.kind === "video" ? videoOpen(config) : imageOpen(config));

export const StudioContext = createContext({ openSearch: () => {} });
export const useStudio = () => useContext(StudioContext);

export const TOOLS = [
  { key: "image-create", label: "Create image", hint: "Turn a description into an image", to: "/create/image", kind: "image" },
  { key: "image-edit", label: "Edit image", hint: "Change a photo with one sentence", to: "/create/image?mode=edit", kind: "image" },
  { key: "video", label: "Video", hint: "Short clips from a description", to: "/create/video", kind: "video" },
  { key: "influencer", label: "AI Influencer", hint: "Saved characters and consistent identities", to: "/create/influencer", kind: "hf" },
  { key: "ads", label: "Ads Studio", hint: "Product shots, posters and product videos", to: "/create/ads", kind: "hf" },
  { key: "models", label: "All models", hint: "Every model with its status and price", to: "/create/models" },
  { key: "assets", label: "Assets", hint: "Everything you've created", to: "/create/assets" },
  { key: "presets", label: "All presets", hint: "Recreate a look with one tap", to: "/create/presets" },
];

export const toolOpen = (tool, config) => (tool.kind === "image" ? imageOpen(config) || hfOpen(config) : tool.kind === "video" ? videoOpen(config) || hfOpen(config) : tool.kind === "hf" ? hfOpen(config) : true);

export const matches = (q, ...fields) => fields.some((f) => f && f.toLowerCase().includes(q));

export const EASE = [0.22, 1, 0.36, 1];

// Honours both the OS setting and ChatSleek's own "Reduce motion" preference.
export function useCalmMotion() {
  const os = useReducedMotion();
  return Boolean(os) || document.documentElement.dataset.reducedMotion === "true";
}

export const compactNumber = (n) => new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(Number(n || 0));

export const useHfCatalog = () => useQuery({ queryKey: ["hf-catalog"], queryFn: () => api.get("/media/hf/catalog"), staleTime: 10 * 60_000 });
export const hfOpen = (config) => Boolean(config?.higgsfield && config.higgsfield.access !== "none");
export const useCharacters = () => useQuery({ queryKey: ["studio-characters"], queryFn: () => api.get("/studio/characters") });
export const STATUS_LABEL = { available: "Ready", price_pending: "Price pending", unverified: "Unverified" };
export const OPERATION_LABEL = {
  "text-to-image": "Text to image", "image-edit": "Edit", "generate-edit": "Generate & edit", "text-to-video": "Text to video",
  "image-to-video": "Image to video", "reference-to-video": "Reference to video", "first-last-frame": "First & last frame",
  "video-edit": "Video edit", "video-extend": "Video extend", "motion-control": "Motion control", ads: "Ads", character: "Character training",
};
